import React, { useState, useEffect } from "react";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomOutlinedInput.js";
import CustomSelect from "components/CustomSelect/CustomSelect.js";
import Button from "components/CustomButtons/CustomButton.js";
import { useTranslation } from "react-i18next";
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import TableHead from "@material-ui/core/TableHead";
import Paper from "@material-ui/core/Paper";

import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import { getRatesCancellation } from "views/Services/CancellationService.js";
import { dateFormatMeridian } from "components/Utils/utils.js";

const COLORS = {
  main: "#13816F",
  headLight: "#19A58D",
  pastel1: "#CFF0EA",
  pastel2: "#E3F7F3",
  faded: "#F3FBF9",
};

const cancellationTypes = [
  { value: "Cancellation", label: "Cancellation" },
  { value: "Substitution", label: "Substitution" },
  { value: "Refund", label: "Refund" },
];

const retainOptions = [
  { value: "No", label: "No" },
  { value: "Yes", label: "Yes" },
];

const cancellationReasons = [
  { value: "Need to reissue - error", label: "Need to reissue - error" },
  { value: "Need more days", label: "Need more days" },
  { value: "Cancelled trip", label: "Cancelled trip" },
  { value: "Duplicate policy", label: "Duplicate policy" },
  { value: "Other", label: "Other" },
];

const today = new Date().toISOString().split("T")[0];
const futureDate = new Date();
futureDate.setDate(futureDate.getDate() + 60);
const sixtyDaysLater = futureDate.toISOString().split("T")[0];

const useStyles = makeStyles(styles);

const Step1Cancellation = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    data = {},
    handleSubmitStep,
    isAdmin = false,
    minDaysForSubstitution = 182,
    minDaysForRefund = 30,
    id,
  } = props;

  useEffect(() => {
    if (props.data) {
      setForm(data);
    }
  }, [props]);

  // Inicializa los datos del formulario con los datos recibidos o vacíos
  const [form, setForm] = useState({
    coverages: {
      csrinitials: "",
      date_cancellation:
        data.date_cancellation ||
        new Date().toISOString().slice(0, 19).replace("T", " "),
      policynumber: data?.coverages?.policy_nbr || "",
      cancellationtype: "",
      clientname: data.clientname || "",
      retainpolicyfee: "No",
      retainagencyfee: "No",
      from_date: data.from_date || "",
      reason: "",
      policynotes: "",
      net_premium: data.net_premium || 0,
      policy_fee: data.policy_fee || 0,
      agency_fee: data.agency_fee || 0,
      iva: data.iva || 0,
      total_refund: data.total_refund || 0,
    },
  });

  const [isCompleted, setIsCompleted] = useState(false);
  const [resultRates, setResultRates] = useState(0);
  const dispatch = useDispatch();

  const getRates = async (cancellationData) => {
    if (cancellationData) {
      let cancellationFromDate = dateFormatMeridian(cancellationData.coverages.from_date);
      let stringRates = {
        type: cancellationData.coverages.cancellationtype,
        policy_fee: cancellationData.coverages.policy_fee,
        ret_policy: cancellationData.coverages.retainpolicyfee,
        agency_fee: cancellationData.coverages.agency_fee,
        ret_agency: cancellationData.coverages.retainagencyfee,
        date_from: cancellationFromDate,
        date_cancellation: cancellationData.coverages.date_cancellation,
        effective_date: cancellationFromDate,
        days: cancellationData.coverages.days,
        premium: cancellationData.coverages.premium,
        iva: cancellationData.coverages.iva,
      };

      const resultSubmit = await dispatch(getRatesCancellation(stringRates));
      if (resultSubmit.payload.result) {
        setResultRates(resultSubmit.payload.info);
      } else {
        let mensaje = "\n";
        for (const key in resultSubmit.payload.message) {
          mensaje += "\u2B24" + " " + resultSubmit.payload.message[key] + "\n";
        }
      }
    }
  };

  useEffect(() => {
    setIsCompleted(
      form?.coverages?.csrinitials.trim() !== "" &&
        form?.coverages?.cancellationtype.trim() !== "" &&
        form?.coverages?.from_date.trim() !== ""
    );
  }, [form]);

  const getCancellationTypeOptions = () => {
    let options = [];
    if (isAdmin) {
      options.push({ value: "Cancellation", label: "Cancellation" });
      if (data.num_of_days >= minDaysForSubstitution) {
        options.push({ value: "Substitution", label: "Substitution" });
      }
      if (data.num_of_days >= minDaysForRefund) {
        options.push({ value: "Refund", label: "Refund" });
      }
    } else {
      options.push({ value: "Cancellation", label: "Cancellation" });
    }
    return options;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      coverages: {
        ...prev.coverages,
        [name]: value,
      },
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      coverages: {
        ...prev.coverages,
        [name]: value,
      },
    }));
  };

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={6} md={3}>
          <CustomInput
            labelText="CSR Initials"
            inputProps={{
              name: "csrinitials",
              value: form?.coverages?.csrinitials,
              onChange: handleInputChange,
              maxLength: 30,
              required: true,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <CustomInput
            labelText="Date"
            inputProps={{
              name: "date_cancellation",
              value: form?.coverages?.date_cancellation,
              readOnly: true,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <CustomInput
            labelText="Policy Number"
            inputProps={{
              name: "policynumber",
              value: form?.coverages?.policynumber,
              readOnly: true,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <CustomSelect
            labelText="Cancellation Type"
            menuItems={getCancellationTypeOptions()}
            inputProps={{
              name: "cancellationtype",
              value: form?.coverages?.cancellationtype,
              onChange: handleSelectChange,
              required: true,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={6}>
          <CustomInput
            labelText="Client Name"
            inputProps={{
              name: "clientname",
              value:
                form?.data_customer?.first_name +
                " " +
                form?.data_customer?.last_name,
              readOnly: true,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <CustomSelect
            labelText="Retain Policy Fee"
            menuItems={retainOptions}
            inputProps={{
              name: "retainpolicyfee",
              value: form?.coverages?.retainpolicyfee,
              onChange: handleSelectChange,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <CustomSelect
            labelText="Retain Agency Fee"
            menuItems={retainOptions}
            inputProps={{
              name: "retainagencyfee",
              value: form?.coverages?.retainagencyfee,
              onChange: handleSelectChange,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={4}>
          <CustomInput
            labelText={t("step4.policyStartDate")}
            formControlProps={{
              fullWidth: true,
              className: classes.customFormControlClasses,
            }}
            min={today}
            inputProps={{
              required: true,
              type: "datetime-local",
              name: "from_date",
              id: "startDateStep4",
              value: form?.coverages?.from_date,
              onChange: handleInputChange,
              max: sixtyDaysLater,
              //min: today,
            }}
          />
        </GridItem>
        {form?.coverages?.cancellationtype === "Cancellation" && (
          <GridItem xs={12} sm={6} md={4}>
            <CustomSelect
              labelText="Reason"
              menuItems={cancellationReasons}
              inputProps={{
                name: "reason",
                value: form?.coverages?.reason,
                onChange: handleSelectChange,
              }}
              formControlProps={{ fullWidth: true }}
            />
          </GridItem>
        )}
        <GridItem xs={12} sm={12} md={12}>
          <CustomInput
            labelText="Comment"
            inputProps={{
              name: "policynotes",
              value: form?.coverages?.policynotes,
              onChange: handleInputChange,
              multiline: true,
              rows: 3,
            }}
            formControlProps={{ fullWidth: true }}
          />
        </GridItem>
        {/* Resumen de reembolso */}
        <GridItem xs={12} sm={12} md={12}>
          <div style={{ marginTop: 24 }}>
            <TableContainer component={Paper}>
              <Table style={{ width: "100%", textAlign: "center" }}>
                <TableHead>
                  <TableRow style={{ background: COLORS.pastel1 }}>
                    <TableCell>Net Premium</TableCell>
                    <TableCell>Policy Fee</TableCell>
                    <TableCell>Agency Fee</TableCell>
                    <TableCell>IVA Tax</TableCell>
                    <TableCell>Total Refund</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      ${Number(resultRates?.net_premium || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      ${Number(resultRates?.policy_fee || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      ${Number(resultRates?.agency_fee || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      ${Number(resultRates?.iva || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      ${Number(resultRates?.total || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {resultRates === 0 && (
              <div
                style={{ color: "#d9534f", textAlign: "center", marginTop: 8 }}
              >
                The cancellation amount is $
                {Number(resultRates.total || 0).toFixed(2)}. If this is not
                correct, make sure to “Calculate” before you proceed.
              </div>
            )}
          </div>
        </GridItem>
      </GridContainer>
      <GridContainer
        container
        justifyContent="space-between"
        alignItems="center"
        style={{ minHeight: "100px", marginTop: "20px" }}
      >
        <GridItem xs={4} sm={4} md={3}>
          <Button
            id="backButton"
            onClick={() => {
              getRates(form);
            }}
            variant="warning"
            style={{ width: "100%" }}
          >
            {t("Calculate")}
          </Button>
        </GridItem>
        <GridItem xs={4} sm={4} md={2} />
        <GridItem xs={4} sm={4} md={3}>
          <Button
            id={"saveQuote"}
            type="submit"
            onClick={() => handleSubmitStep(form, resultRates)}
            disabled={!isCompleted}
            variant="green"
            style={{ width: "100%" }}
          >
            {t("saveQuote")}
          </Button>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default Step1Cancellation;
